import { createFormHookContexts, useStore, type AnyFieldApi } from '@tanstack/react-form';

/**
 * Subscribe to a field's value using Object.is equality.
 * Needed for File-containing fields where shallow equality fails (zero own keys),
 * and for React Compiler compatibility (explicit store subscription).
 *
 * Uses `any` casts internally to work around Derived/ReadonlyStore not satisfying
 * useStore's AnyAtom constraint across @tanstack/store versions.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useFieldValue<T>(field: { store: any; state: { value: T } }): T {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	return useStore(field.store, (state: any) => state.value, Object.is as any);
}

export function hasFieldError(meta: AnyFieldApi['state']['meta']): boolean {
	if (meta.isValid) return false;
	const hasSubmitErrors = (meta.errorMap as Record<string, unknown[]> | undefined)?.onSubmit?.length;
	return meta.isTouched || !!hasSubmitErrors;
}

/**
 * The text `FieldInfo` would render, for a field that owns its own message slot. `AmountInput` is
 * one: it renders the message itself so it can point `aria-describedby` at it.
 */
export function fieldErrorMessage(fieldState: AnyFieldApi['state']): string | undefined {
	const { meta } = fieldState;
	if (!hasFieldError(meta)) return undefined;
	return meta.errors.map((error: Error) => error.message).join(', ') || undefined;
}

// TODO: We're passing field.state instead of field to avoid this bad behavior of tanstack form with react compiler:
// https://github.com/TanStack/form/issues/1379
export function FieldInfo(props: { fieldState: AnyFieldApi['state'] }) {
	const message = fieldErrorMessage(props.fieldState);
	if (message == null) return null;
	return (
		<p data-slot="form-message" className="text-destructive m-1 text-sm">
			{message}
		</p>
	);
}

export const { fieldContext, formContext, useFieldContext, useFormContext } = createFormHookContexts();

/**
 * Check whether a resolved Zod schema represents a required field.
 * A field is required if it has meaningful validation constraints
 * (e.g. min length, positive minimum, enum membership).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function hasRequiredConstraint(schema: any): boolean {
	const def = schema._zod.def;
	if (def.type === 'enum') return true;
	// z.literal() values are inherently required (must match exactly)
	if (def.type === 'literal') return true;
	// z.union() of values (e.g. z.union([z.literal('a'), z.literal('b')])) is required
	if (def.type === 'union') return true;
	// Format-constrained strings (e.g. z.email(), z.url()) are inherently non-empty
	if (def.check === 'string_format') return true;

	const checks: { _zod: { def: Record<string, unknown> } }[] | undefined = def.checks;
	if (!checks?.length) return false;

	return checks.some((c) => {
		const d = c._zod.def;
		if (d.check === 'min_length') return (d.minimum as number) >= 1;
		if (d.check === 'greater_than') return d.inclusive ? (d.value as number) >= 1 : (d.value as number) > 0;
		return false;
	});
}

/**
 * Traverse a Zod v4 schema by dot/bracket path and check if the target field is optional.
 * Uses Zod's internal `_zod` introspection API.
 * Returns `true` if optional, `false` if required, `undefined` if the path can't be resolved.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isOptionalInSchema(schema: any, fieldPath: string): boolean | undefined {
	const parts = fieldPath.replace(/\[(\d+)\]/g, '.$1').split('.');
	let current = schema;

	for (const part of parts) {
		if (!current?._zod) return undefined;
		const defType = current._zod.def.type;

		if (defType === 'object' && current._zod.def.shape) {
			current = current._zod.def.shape[part];
		} else if (defType === 'array' && /^\d+$/.test(part)) {
			current = current._zod.def.element;
		} else if (defType === 'record') {
			// If the record itself has checks (e.g. a .check() requiring content for translatableText), values are required
			if (current._zod.def.checks?.length) return false;
			current = current._zod.def.valueType;
		} else {
			return undefined;
		}
	}

	if (!current?._zod) return undefined;
	if (current._zod.optin === 'optional') return true;
	return !hasRequiredConstraint(current);
}

/**
 * Hook to determine if a field is optional based on the form-level Zod validator schema.
 * Falls back to checking the field-level validator when no form schema is available.
 */
export function useIsFieldOptional(fieldName: string, explicitOptional?: boolean): boolean {
	const field = useFieldContext();
	if (explicitOptional !== undefined) return explicitOptional;

	// Try form-level Zod schema introspection (check all validator slots)
	const validators = field.form.options.validators;
	const formSchema = validators?.onSubmit ?? validators?.onChange ?? validators?.onBlur;
	if (formSchema) {
		const result = isOptionalInSchema(formSchema, fieldName);
		if (result !== undefined) return result;
	}

	// Fallback: field is optional if it has no field-level validators
	return !(
		field.options.validators?.onSubmit ??
		field.options.validators?.onChange ??
		field.options.validators?.onBlur
	);
}
